import {
  type ResilientStorageMode,
  readResilientStorageRaw,
  writeResilientStorage,
} from '@ctw/shared/utils/resilient-storage-provider';
import { useCallback, useSyncExternalStore } from 'react';

const subscribe = (callback: () => void) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

// Pure helper kept at module scope so it has no closure over hook-local values —
// that keeps the exhaustive-deps analysis honest without suppressions.
const parseRawValue = <T>(raw: string | undefined, fallback: T): T => {
  if (raw === undefined) {
    return fallback;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

export type ResilientStorageSetter<T> = (value: T | ((previousValue: T) => T)) => void;

interface UseResilientStorageOptions {
  mode: ResilientStorageMode;
}

export const useResilientStorage = <T>(
  key: string,
  initialValue: T,
  { mode }: UseResilientStorageOptions,
): [T, ResilientStorageSetter<T>] => {
  const getSnapshot = useCallback(() => readResilientStorageRaw(mode, key), [mode, key]);
  const rawValue = useSyncExternalStore(subscribe, getSnapshot);

  const setValue = useCallback<ResilientStorageSetter<T>>(
    (value) => {
      const currentValue = parseRawValue(rawValue, initialValue);
      const nextValue =
        typeof value === 'function' ? (value as (previousValue: T) => T)(currentValue) : value;
      writeResilientStorage(mode, key, nextValue);
    },
    [mode, key, rawValue, initialValue],
  );

  return [parseRawValue(rawValue, initialValue), setValue];
};
